"use client"

import { HttpTypes } from "@medusajs/types"
import { Text } from "@modules/common/components/ui"
import NotifyRestockForm from "./notify-restock-form"

// Lead-time copy per variant metadata.backorder_class, used when no concrete
// restock_eta container date is set on the variant.
const LEAD_TIME_BY_CLASS: Record<string, string> = {
  radiator:
    "A production run is scheduled. We'll email your exact ship date as soon as it's confirmed — cancel anytime.",
  shroud: "Typically ships within 10–14 days.",
  "champion-fan": "Typically ships within 3–4 weeks.",
  spal: "Extended lead time — we'll email your confirmed ship date. Cancel anytime.",
  standard:
    "We'll email your expected ship date after checkout. Cancel anytime.",
}

const DEFAULT_LEAD_TIME = LEAD_TIME_BY_CLASS.standard

// Formats a "YYYY-MM-DD" restock date without timezone drift.
const formatEta = (eta: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(eta)

  if (!match) {
    return null
  }

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  )

  if (isNaN(date.getTime())) {
    return null
  }

  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

type BackorderPanelProps = {
  variant: HttpTypes.StoreProductVariant
}

// Amber reassurance block shown between the price and the add-to-cart button
// when the selected variant is out of stock but backorderable.
export default function BackorderPanel({ variant }: BackorderPanelProps) {
  const restockEta =
    typeof variant.metadata?.restock_eta === "string"
      ? formatEta(variant.metadata.restock_eta)
      : null

  const backorderClass =
    typeof variant.metadata?.backorder_class === "string"
      ? variant.metadata.backorder_class
      : "standard"

  const leadTime = restockEta
    ? `Expected to ship by ${restockEta}.`
    : LEAD_TIME_BY_CLASS[backorderClass] ?? DEFAULT_LEAD_TIME

  return (
    <div
      role="status"
      className="flex flex-col gap-y-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-amber-800"
      data-testid="backorder-panel"
    >
      <Text className="text-sm font-semibold" data-testid="backorder-headline">
        Backordered &mdash; reserve yours now
      </Text>
      <Text className="text-sm" data-testid="backorder-lead-time">
        {leadTime}
      </Text>
      <Text className="text-sm" data-testid="backorder-trust-line">
        Backorders are fulfilled first &mdash; ahead of all new stock &mdash;
        the moment inventory arrives.
      </Text>
      {variant.sku && <NotifyRestockForm sku={variant.sku} />}
    </div>
  )
}
